import React from 'react'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import Autocomplete from '@mui/material/Autocomplete'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Checkbox from '@mui/material/Checkbox'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { UI_COLOR_ICON_CHEVRON, UI_COLOR_TEXT_PLACEHOLDER } from '../../../constants/uiColors'
import {
  dialogAvatarCompactSx,
  dialogCheckboxSx,
  dialogCurrentRoleLabelSx,
  dialogEmailTextSx,
  dialogNameTextSx
} from '../../../constants/dialogSx'
import { RbacRoleChip } from '../RbacChips'
import {
  getRbacUserDisplayName,
  getRbacUserInitials,
  getRbacUserPrimaryRoleName
} from '../../pages/shared/rbacUserDetailHelpers'
import {
  RBAC_BULK_REASSIGN_NO_ROLES_TEXT,
  RBAC_BULK_REASSIGN_ROLE_PLACEHOLDER,
  RBAC_BULK_REASSIGN_UNASSIGNED_ROLE_LABEL,
  RBAC_BULK_REASSIGN_UNSELECTED_ROW_TEXT,
  rbacBulkReassignChevronSx,
  rbacBulkReassignHintTextSx,
  rbacBulkReassignRoleFieldSx,
  rbacBulkReassignRowSx
} from './constants'

export default function RbacBulkReassignUserRow({
  user,
  selected,
  roles,
  fixedRole,
  hasFixedRole,
  rowError,
  selectedRoleIds,
  onToggleUser,
  onRoleChange
}) {
  const selectedRole =
    (hasFixedRole ? fixedRole : roles.find((role) => selectedRoleIds.includes(role.id))) || null
  const primaryRoleName = getRbacUserPrimaryRoleName(user)
  const hasUnassignedRole = primaryRoleName === RBAC_BULK_REASSIGN_UNASSIGNED_ROLE_LABEL
  const displayName = getRbacUserDisplayName(user)

  return (
    <Box sx={rbacBulkReassignRowSx(selected)}>
      <Checkbox
        checked={selected}
        onChange={() => onToggleUser(user.id)}
        inputProps={{ 'aria-label': `Seleccionar a ${displayName}` }}
        sx={dialogCheckboxSx}
      />
      <Avatar sx={dialogAvatarCompactSx}>{getRbacUserInitials(displayName)}</Avatar>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={dialogNameTextSx}>{displayName}</Typography>
        <Typography sx={dialogEmailTextSx}>{user.email || '—'}</Typography>
      </Box>
      <Box sx={{ minWidth: 0, gridColumn: { xs: '1 / -1', md: 'auto' } }}>
        <Typography sx={dialogCurrentRoleLabelSx}>Rol actual</Typography>
        <Box sx={{ mt: 0.5 }}>
          <RbacRoleChip
            label={hasUnassignedRole ? RBAC_BULK_REASSIGN_UNASSIGNED_ROLE_LABEL : primaryRoleName}
            unassigned={hasUnassignedRole}
          />
        </Box>
      </Box>
      <Box sx={{ ...rbacBulkReassignChevronSx, color: UI_COLOR_ICON_CHEVRON }}>
        <ArrowForwardIcon fontSize="small" />
      </Box>
      <Box sx={{ minWidth: 0, gridColumn: { xs: '1 / -1', md: 'auto' } }}>
        {selected && !hasFixedRole ? (
          <Autocomplete
            options={roles}
            value={selectedRole}
            size="small"
            fullWidth
            getOptionLabel={(option) => option.name || ''}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(_event, nextRole) => onRoleChange(user.id, nextRole)}
            noOptionsText={RBAC_BULK_REASSIGN_NO_ROLES_TEXT}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={RBAC_BULK_REASSIGN_ROLE_PLACEHOLDER}
                size="small"
                error={Boolean(rowError)}
                helperText={rowError}
                hiddenLabel
                InputLabelProps={{ ...params.InputLabelProps, shrink: false }}
                sx={rbacBulkReassignRoleFieldSx}
              />
            )}
          />
        ) : selected && hasFixedRole ? (
          <RbacRoleChip label={fixedRole.name || RBAC_BULK_REASSIGN_UNASSIGNED_ROLE_LABEL} />
        ) : (
          <Typography sx={{ ...rbacBulkReassignHintTextSx, color: UI_COLOR_TEXT_PLACEHOLDER }}>
            {RBAC_BULK_REASSIGN_UNSELECTED_ROW_TEXT}
          </Typography>
        )}
      </Box>
    </Box>
  )
}
